import cuid from "cuid";

import type { AssignmentRole, AssignmentStatus, TmsEvent } from "@storyboard/web-client";
import { isStaleAt } from "@/models/repositories/AssignmentInterval";
import { bulkUpsertTmsEvent } from "@/models/repositories/TmsEvent";
import { idx, rowsByIndex, store } from "@/store/store";
import type { TmsEventRow } from "@/store/types";

/** One assignment record as the debug console lists it, to answer why an event landed in a driver's feed. */
export type AssignmentIntervalDebugRow = {
  /** The driver's name, or the membership id when the store holds no name for it. */
  driver: string;
  unit: string;
  role: AssignmentRole;
  start: string;
  end: string | null;
  status: AssignmentStatus;
  /** An active record open past the staleness threshold: a missed logout that no longer covers the present. */
  stale: boolean;
  membershipId: string;
  integrationId: string;
  openedBy: string;
};

/** What a synthetic vehicle event is placed at: a unit and an instant, with no driver of its own. */
export type SyntheticVehicleEventInput = {
  workspaceId: string;
  vendorVehicleKey: string;
  occurredAt: string;
  /** The integration whose records it joins; defaults to the one integration holding records on the unit. */
  workspaceIntegrationId?: string;
};

/**
 * Every assignment record the store holds, by unit and then start, as of `now`: exactly what attribution reads, which
 * the sync fills from the active workspace alone.
 *
 * @param now - The instant staleness is judged at.
 * @param nameOf - A driver's display name, or null when the store cannot name one.
 */
export function assignmentIntervalDebugRows(
  now: string,
  nameOf: (membershipId: string) => string | null,
): AssignmentIntervalDebugRow[] {
  return [...store.getAll("assignmentInterval").values()]
    .map((row) => ({
      driver: nameOf(row.workspaceMembershipId) ?? row.workspaceMembershipId,
      unit: row.vendorVehicleKey,
      role: row.role,
      start: row.start,
      end: row.end,
      status: row.status,
      stale: isStaleAt(row, now),
      membershipId: row.workspaceMembershipId,
      integrationId: row.workspaceIntegrationId,
      openedBy: row.openedBy,
    }))
    .sort((a, b) => a.unit.localeCompare(b.unit) || a.start.localeCompare(b.start));
}

/**
 * Lands a synthetic arrival on a unit at an instant, naming no driver, and returns it as the store attributed it.
 *
 * The only way to exercise the assignment join until a vehicle-scoped kind ships. It writes to the local store alone:
 * nothing reaches the server, and no sync removes it; {@link removeSyntheticEvents} does.
 *
 * @param input - The unit, instant and workspace the event is placed at.
 * @param now - Stamped as the event's `createdAt`, the server's clock as the client estimates it.
 * @throws {Error} when no integration is named and the unit's records do not settle on exactly one.
 */
export async function injectSyntheticVehicleEvent(
  input: SyntheticVehicleEventInput,
  now: string,
): Promise<TmsEventRow> {
  const event: TmsEvent = {
    id: `${SYNTHETIC_ID_PREFIX}${cuid()}`,
    subject: "StopEvent",
    occurredAt: input.occurredAt,
    createdAt: now,
    workspaceId: input.workspaceId,
    workspaceIntegrationId: input.workspaceIntegrationId ?? integrationOnUnit(input.vendorVehicleKey),
    vendorVehicleKey: input.vendorVehicleKey,
    completedByWorkspaceMembershipId: null,
    category: "movement",
    kind: "arrival",
    version: 1,
    stopName: "Synthetic vehicle event",
    city: null,
    stopRole: "stop",
  };
  await bulkUpsertTmsEvent([event]);
  const stored = store.get("tmsEvent", event.id);
  if (stored === undefined) throw new Error(`Synthetic event ${event.id} did not land in the store`);
  return stored;
}

/** The id prefix every synthetic event carries, so the debug tools can find and remove what they injected. */
const SYNTHETIC_ID_PREFIX = "synthetic-";

/**
 * Removes every synthetic event from the store, returning how many it removed.
 *
 * Injected events persist in the local snapshot like any other row, so this is how a session cleans up after itself.
 */
export function removeSyntheticEvents(): number {
  const ids = [...store.getAll("tmsEvent").keys()].filter((id) => id.startsWith(SYNTHETIC_ID_PREFIX));
  store.delete("tmsEvent", ids);
  return ids.length;
}

/** The one integration holding records on the unit. */
function integrationOnUnit(vendorVehicleKey: string): string {
  const integrations = new Set(
    rowsByIndex("assignmentInterval", idx.assignmentIntervalsByVehicle, vendorVehicleKey).map(
      (row) => row.workspaceIntegrationId,
    ),
  );
  if (integrations.size !== 1) {
    throw new Error(
      `Unit ${vendorVehicleKey} has records in ${integrations.size} integrations; pass workspaceIntegrationId`,
    );
  }
  return [...integrations][0];
}
